import React, { useEffect, useState } from "react";
import {
  Outlet,
  NavLink,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";

type AuthMeResponse = {
  clientPrincipal: null | {
    userDetails?: string;
    identityProvider?: string;
  };
};

type BootstrapRole = {
  roleName?: string | null;
  isActive?: boolean | number | string | null;
};

type BootstrapResponse = {
  status?: "Pending" | "Approved" | "Disabled" | string;
  roles?: BootstrapRole[];
};

type GuardState =
  | { status: "checking" }
  | { status: "allowed" }
  | { status: "redirect"; to: string };

/**
 * SuperAdminLayout — production portal shell
 *
 * Purpose:
 * - Shell + sidebar navigation for Super Admin pages
 * - Matches the darker fixed-height portal sidebar pattern used elsewhere
 * - Keeps Super Admin governance areas visible
 * - Adds lightweight client-side SuperAdmin guard before rendering the shell
 *
 * IA:
 * Dashboard
 * Approvals
 * Clinics
 * OHTs
 * System Health
 * Activity Log
 * Exit Super Admin -> /portal/select-role
 *
 * Additional Super Admin areas can be added as the portal expands.
 */

function normRoleName(name: unknown): string {
  return String(name || "").trim().toLowerCase();
}

function isRoleActive(value: unknown): boolean {
  // Missing isActive means active for older bootstrap payloads.
  if (value === undefined || value === null) return true;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  return String(value).trim().toLowerCase() === "true";
}

function hasActiveSuperAdminRole(roles: BootstrapRole[] | undefined): boolean {
  if (!Array.isArray(roles)) return false;

  return roles.some(
    (role) =>
      normRoleName(role?.roleName) === "superadmin" &&
      isRoleActive(role?.isActive)
  );
}

function buildLoginUrl(returnTo: string): string {
  return `/portal/login?returnTo=${encodeURIComponent(returnTo)}`;
}

function LoadingGuardScreen() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background:
          "radial-gradient(circle at top left, rgba(185, 28, 28, 0.08), transparent 32%), linear-gradient(135deg, #f8fafc 0%, #eef4fb 100%)",
        color: "#102a43",
        fontFamily: "system-ui, -apple-system, Segoe UI, sans-serif",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "min(460px, 100%)",
          borderRadius: 22,
          border: "1px solid rgba(148, 163, 184, 0.28)",
          background: "#ffffff",
          boxShadow: "0 18px 42px rgba(15, 23, 42, 0.08)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 16,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            display: "grid",
            placeItems: "center",
            margin: "0 auto 13px",
            color: "#991b1b",
            fontWeight: 750,
            boxShadow: "0 10px 22px rgba(185, 28, 28, 0.08)",
          }}
          aria-hidden
        >
          SA
        </div>

        <div
          style={{
            fontSize: 17,
            fontWeight: 750,
            color: "#102a43",
            letterSpacing: "-0.01em",
          }}
        >
          Checking Super Admin access
        </div>

        <div
          style={{
            marginTop: 7,
            fontSize: 13,
            color: "#64748b",
            lineHeight: 1.55,
            fontWeight: 450,
          }}
        >
          Confirming your active portal role before loading the governance
          workspace.
        </div>
      </div>
    </div>
  );
}

const SUPER_ADMIN_RESPONSIVE_CSS = `
  .super-admin-shell,
  .super-admin-shell * {
    box-sizing: border-box;
  }

  .super-admin-sidebar::-webkit-scrollbar,
  .super-admin-main::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  .super-admin-sidebar::-webkit-scrollbar-track,
  .super-admin-main::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.08);
  }

  .super-admin-sidebar::-webkit-scrollbar-thumb {
    background: rgba(226, 232, 240, 0.24);
    border-radius: 999px;
  }

  .super-admin-main::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.55);
    border-radius: 999px;
  }

  .super-admin-nav-link {
    transition:
      background 160ms ease,
      border-color 160ms ease,
      color 160ms ease,
      transform 160ms ease,
      box-shadow 160ms ease;
  }

  .super-admin-exit-link,
  .super-admin-header-link {
    transition:
      background 160ms ease,
      border-color 160ms ease,
      color 160ms ease,
      transform 160ms ease,
      box-shadow 160ms ease;
  }

  @media (hover: hover) {
    .super-admin-nav-link:hover {
      transform: translateX(2px);
      background: rgba(255, 255, 255, 0.11) !important;
      border-color: rgba(255, 255, 255, 0.16) !important;
      color: #ffffff !important;
    }

    .super-admin-exit-link:hover {
      transform: translateY(-1px);
      background: rgba(255, 255, 255, 0.12) !important;
      border-color: rgba(255, 255, 255, 0.2) !important;
      color: #ffffff !important;
    }

    .super-admin-header-link:hover {
      transform: translateY(-1px);
      background: #fff7f7 !important;
      border-color: rgba(185, 28, 28, 0.24) !important;
      color: #991b1b !important;
      box-shadow: 0 10px 20px rgba(15, 23, 42, 0.06);
    }
  }

  .super-admin-nav-link:focus-visible,
  .super-admin-exit-link:focus-visible,
  .super-admin-header-link:focus-visible {
    outline: 3px solid rgba(248, 113, 113, 0.55);
    outline-offset: 2px;
  }

  @media (max-width: 1100px) {
    .super-admin-shell {
      grid-template-columns: 250px minmax(0, 1fr) !important;
    }

    .super-admin-main {
      padding: 18px !important;
    }
  }

  @media (max-width: 920px) {
    .super-admin-shell {
      display: block !important;
      min-height: 100vh !important;
      height: auto !important;
      max-height: none !important;
      overflow: auto !important;
    }

    .super-admin-sidebar {
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      border-right: 0 !important;
      border-bottom: 1px solid rgba(15, 23, 42, 0.22) !important;
      padding: 14px !important;
      box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12) !important;
    }

    .super-admin-identity-card {
      min-height: 58px !important;
      padding: 12px !important;
    }

    .super-admin-banner {
      margin-top: 10px !important;
    }

    .super-admin-nav {
      display: grid !important;
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 10px !important;
      margin-top: 12px !important;
    }

    .super-admin-nav-section {
      margin-bottom: 0 !important;
      gap: 6px !important;
    }

    .super-admin-section-label {
      padding: 6px 8px 2px !important;
    }

    .super-admin-nav-link {
      min-height: 44px !important;
      padding: 10px !important;
    }

    .super-admin-sidebar-bottom {
      margin-top: 12px !important;
      padding-top: 0 !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) auto !important;
      align-items: center !important;
      gap: 10px !important;
    }

    .super-admin-action-card {
      padding: 8px !important;
    }

    .super-admin-content-shell {
      height: auto !important;
      max-height: none !important;
      min-height: 0 !important;
      overflow: visible !important;
    }

    .super-admin-header {
      padding: 12px 14px !important;
    }

    .super-admin-main {
      min-height: 0 !important;
      overflow: visible !important;
      padding: 16px 14px !important;
    }
  }

  @media (max-width: 680px) {
    .super-admin-nav {
      grid-template-columns: 1fr !important;
    }

    .super-admin-nav-section {
      border-radius: 16px !important;
      padding: 8px !important;
      background: rgba(255, 255, 255, 0.055) !important;
      border: 1px solid rgba(255, 255, 255, 0.09) !important;
    }

    .super-admin-sidebar-bottom {
      grid-template-columns: 1fr !important;
    }

    .super-admin-header-inner {
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 10px !important;
    }

    .super-admin-header-link {
      width: 100% !important;
      display: flex !important;
      justify-content: center !important;
      text-align: center !important;
    }

    .super-admin-main {
      padding: 14px 12px !important;
    }
  }

  @media (max-width: 420px) {
    .super-admin-sidebar {
      padding: 12px !important;
    }

    .super-admin-identity-card {
      align-items: flex-start !important;
    }

    .super-admin-avatar {
      width: 36px !important;
      height: 36px !important;
      border-radius: 13px !important;
    }

    .super-admin-banner {
      font-size: 11.5px !important;
    }
  }
`;

export default function SuperAdminLayout() {
  const location = useLocation();
  const [guardState, setGuardState] = useState<GuardState>({
    status: "checking",
  });

  useEffect(() => {
    let cancelled = false;

    const checkAccess = async () => {
      const returnTo = `${location.pathname}${location.search}${location.hash}`;

      try {
        const meRes = await fetch("/.auth/me", { credentials: "include" });
        const me = (await meRes.json()) as AuthMeResponse;

        if (cancelled) return;

        if (!me?.clientPrincipal) {
          setGuardState({
            status: "redirect",
            to: buildLoginUrl(returnTo),
          });
          return;
        }

        const bootRes = await fetch("/api/portal-auth-bootstrap", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (cancelled) return;

        if (!bootRes.ok) {
          setGuardState({ status: "redirect", to: "/portal/no-access" });
          return;
        }

        const boot = (await bootRes.json()) as BootstrapResponse;

        if (cancelled) return;

        if (boot.status === "Pending") {
          setGuardState({ status: "redirect", to: "/portal/access-pending" });
          return;
        }

        if (boot.status === "Disabled") {
          setGuardState({ status: "redirect", to: "/portal/access-disabled" });
          return;
        }

        if (!hasActiveSuperAdminRole(boot.roles)) {
          setGuardState({ status: "redirect", to: "/portal/no-access" });
          return;
        }

        setGuardState({ status: "allowed" });
      } catch {
        if (!cancelled) {
          setGuardState({ status: "redirect", to: "/portal/no-access" });
        }
      }
    };

    checkAccess();

    return () => {
      cancelled = true;
    };
  }, [location.hash, location.pathname, location.search]);

  if (guardState.status === "checking") {
    return <LoadingGuardScreen />;
  }

  if (guardState.status === "redirect") {
    return <Navigate to={guardState.to} replace />;
  }

  const shellStyle: React.CSSProperties = {
    height: "100vh",
    maxHeight: "100vh",
    overflow: "hidden",
    background:
      "radial-gradient(circle at top right, rgba(185, 28, 28, 0.055), transparent 34%), linear-gradient(135deg, #f8fafc 0%, #eef4fb 100%)",
    color: "#102a43",
    fontFamily: "system-ui, -apple-system, Segoe UI, sans-serif",
    display: "grid",
    gridTemplateColumns: "280px minmax(0, 1fr)",
    WebkitFontSmoothing: "antialiased",
    textRendering: "geometricPrecision",
  };

  const sidebarStyle: React.CSSProperties = {
    height: "100vh",
    maxHeight: "100vh",
    overflowY: "auto",
    overflowX: "hidden",
    background:
      "radial-gradient(circle at top left, rgba(248, 113, 113, 0.24), transparent 34%), linear-gradient(180deg, #182234 0%, #101827 48%, #0b1220 100%)",
    borderRight: "1px solid rgba(15, 23, 42, 0.72)",
    padding: "16px 12px",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    boxShadow: "14px 0 34px rgba(15, 23, 42, 0.12)",
    zIndex: 5,
  };

  const identityCardStyle: React.CSSProperties = {
    minWidth: 0,
    minHeight: 66,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "13px 14px",
    borderRadius: 18,
    background: "rgba(255, 255, 255, 0.09)",
    border: "1px solid rgba(255, 255, 255, 0.13)",
    boxShadow: "0 14px 28px rgba(4, 14, 25, 0.16)",
    backdropFilter: "blur(10px)",
  };

  const avatarStyle: React.CSSProperties = {
    width: 40,
    height: 40,
    flex: "0 0 auto",
    borderRadius: 14,
    background: "rgba(254, 226, 226, 0.15)",
    border: "1px solid rgba(254, 202, 202, 0.26)",
    display: "grid",
    placeItems: "center",
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 750,
    letterSpacing: "-0.02em",
    boxShadow: "0 10px 20px rgba(185, 28, 28, 0.12)",
  };

  const bannerStyle: React.CSSProperties = {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 16,
    background: "rgba(185, 28, 28, 0.15)",
    border: "1px solid rgba(254, 202, 202, 0.2)",
    color: "rgba(255, 241, 242, 0.88)",
    fontSize: 12,
    lineHeight: 1.45,
    fontWeight: 500,
  };

  const navStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: 16,
    minWidth: 0,
  };

  const navSectionStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginBottom: 10,
    minWidth: 0,
  };

  const sectionLabelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.055em",
    textTransform: "uppercase",
    color: "rgba(226, 232, 240, 0.58)",
    padding: "8px 10px 3px 10px",
  };

  const navItemStyle = (isActive: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "10px 11px",
    borderRadius: 14,
    textDecoration: "none",
    fontSize: 14,
    fontWeight: 650,
    lineHeight: 1.25,
    color: isActive ? "#ffffff" : "#dbeafe",
    background: isActive ? "rgba(255, 255, 255, 0.16)" : "transparent",
    border: isActive
      ? "1px solid rgba(254, 202, 202, 0.32)"
      : "1px solid transparent",
    boxShadow: isActive
      ? "inset 3px 0 0 rgba(248, 113, 113, 0.92), 0 10px 22px rgba(4, 14, 25, 0.12)"
      : "none",
    cursor: "pointer",
    userSelect: "none",
    minWidth: 0,
    gap: 10,
  });

  const navLabelStyle: React.CSSProperties = {
    minWidth: 0,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };

  const subLabelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 800,
    padding: "2px 8px",
    borderRadius: 999,
    background: "#fecdd3",
    color: "#7f1d1d",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    whiteSpace: "nowrap",
    flex: "0 0 auto",
  };

  const sidebarBottomStyle: React.CSSProperties = {
    marginTop: "auto",
    paddingTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
  };

  const actionCardStyle: React.CSSProperties = {
    minWidth: 0,
    borderRadius: 16,
    background: "rgba(255, 255, 255, 0.07)",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    boxShadow: "0 12px 24px rgba(4, 14, 25, 0.14)",
    padding: 10,
  };

  const exitLinkStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minWidth: 0,
    textDecoration: "none",
    padding: "10px 10px",
    borderRadius: 13,
    border: "1px solid rgba(255, 255, 255, 0.12)",
    background: "rgba(255, 255, 255, 0.075)",
    color: "#dbeafe",
    fontWeight: 650,
    fontSize: 13,
    textAlign: "center",
  };

  const contentShellStyle: React.CSSProperties = {
    height: "100vh",
    maxHeight: "100vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  };

  const headerStyle: React.CSSProperties = {
    flex: "0 0 auto",
    background: "rgba(255, 255, 255, 0.92)",
    borderBottom: "1px solid rgba(148, 163, 184, 0.28)",
    padding: "14px 22px",
    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.045)",
    backdropFilter: "blur(12px)",
  };

  const mainStyle: React.CSSProperties = {
    flex: "1 1 auto",
    minHeight: 0,
    minWidth: 0,
    overflowY: "auto",
    overflowX: "hidden",
    padding: 24,
  };

  return (
    <>
      <style>{SUPER_ADMIN_RESPONSIVE_CSS}</style>

      <div className="super-admin-shell" style={shellStyle}>
        {/* Sidebar */}
        <aside
          className="super-admin-sidebar"
          style={sidebarStyle}
          aria-label="Super Admin portal navigation"
        >
          {/* Brand / Title */}
          <div>
            <div className="super-admin-identity-card" style={identityCardStyle}>
              <div
                className="super-admin-avatar"
                style={avatarStyle}
                aria-hidden
                title="Super Admin"
              >
                SA
              </div>

              <div style={{ minWidth: 0, lineHeight: 1.2 }}>
                <div
                  style={{
                    fontWeight: 750,
                    fontSize: 15,
                    color: "#f8fafc",
                    marginBottom: 5,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    letterSpacing: "-0.01em",
                  }}
                >
                  Super Admin
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(226, 232, 240, 0.72)",
                    fontWeight: 550,
                    overflowWrap: "anywhere",
                  }}
                >
                  Platform governance portal
                </div>
              </div>
            </div>

            {/* Break-glass banner */}
            <div className="super-admin-banner" style={bannerStyle}>
              You are operating as{" "}
              <span style={{ color: "#ffffff", fontWeight: 800 }}>
                Super Admin
              </span>
              . Use governance actions carefully.
            </div>
          </div>

          {/* Nav */}
          <nav className="super-admin-nav" style={navStyle}>
            <div className="super-admin-nav-section" style={navSectionStyle}>
              <div className="super-admin-section-label" style={sectionLabelStyle}>
                Workspace
              </div>

              <NavLink
                className="super-admin-nav-link"
                to="/portal/super-admin/dashboard"
                style={({ isActive }) => navItemStyle(isActive)}
              >
                <span style={navLabelStyle}>Dashboard</span>
              </NavLink>
            </div>

            <div className="super-admin-nav-section" style={navSectionStyle}>
              <div className="super-admin-section-label" style={sectionLabelStyle}>
                Governance
              </div>

              <NavLink
                className="super-admin-nav-link"
                to="/portal/super-admin/approvals"
                style={({ isActive }) => navItemStyle(isActive)}
              >
                <span style={navLabelStyle}>Approvals</span>
                <span style={subLabelStyle}>Action</span>
              </NavLink>

              <NavLink
                className="super-admin-nav-link"
                to="/portal/super-admin/clinics"
                end={false}
                style={({ isActive }) => navItemStyle(isActive)}
              >
                <span style={navLabelStyle}>Clinics</span>
              </NavLink>

              <NavLink
                className="super-admin-nav-link"
                to="/portal/super-admin/ohts"
                end={false}
                style={({ isActive }) => navItemStyle(isActive)}
              >
                <span style={navLabelStyle}>OHTs</span>
              </NavLink>
            </div>

            <div className="super-admin-nav-section" style={navSectionStyle}>
              <div className="super-admin-section-label" style={sectionLabelStyle}>
                Safety
              </div>

              <NavLink
                className="super-admin-nav-link"
                to="/portal/super-admin/system-health"
                style={({ isActive }) => navItemStyle(isActive)}
              >
                <span style={navLabelStyle}>System Health</span>
              </NavLink>

              <NavLink
                className="super-admin-nav-link"
                to="/portal/super-admin/activity"
                style={({ isActive }) => navItemStyle(isActive)}
              >
                <span style={navLabelStyle}>Activity Log</span>
              </NavLink>
            </div>
          </nav>

          {/* Exit */}
          <div
            className="super-admin-sidebar-bottom"
            style={sidebarBottomStyle}
          >
            <div className="super-admin-action-card" style={actionCardStyle}>
              <Link
                className="super-admin-exit-link"
                to="/portal/select-role"
                style={exitLinkStyle}
              >
                Exit Super Admin
              </Link>
            </div>

            <div
              style={{
                fontSize: 12,
                color: "rgba(226, 232, 240, 0.56)",
                textAlign: "center",
                fontWeight: 550,
              }}
            >
              © {new Date().getFullYear()} AcceptingMD
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div
          className="super-admin-content-shell"
          style={contentShellStyle}
        >
          {/* Top strip */}
          <header className="super-admin-header" style={headerStyle}>
            <div
              className="super-admin-header-inner"
              style={{
                maxWidth: 1280,
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 16,
                    color: "#102a43",
                    letterSpacing: "-0.01em",
                  }}
                >
                  Super Admin Portal
                </div>
                <div
                  style={{ fontSize: 13, color: "#64748b", fontWeight: 550 }}
                >
                  Platform governance and system oversight
                </div>
              </div>

              <Link
                className="super-admin-header-link"
                to="/portal"
                style={{
                  textDecoration: "none",
                  color: "#102a43",
                  fontWeight: 750,
                  fontSize: 13,
                  padding: "9px 12px",
                  borderRadius: 13,
                  border: "1px solid rgba(148, 163, 184, 0.35)",
                  background: "#ffffff",
                  whiteSpace: "nowrap",
                  boxShadow: "0 8px 18px rgba(15, 23, 42, 0.04)",
                }}
                title="Return to portal landing"
              >
                Portal Home
              </Link>
            </div>
          </header>

          <main className="super-admin-main" style={mainStyle}>
            <div style={{ maxWidth: 1280, width: "100%", margin: "0 auto" }}>
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </>
  );
}